import { Package, Server, Database, Activity, Layers, AlertTriangle, Filter, Brain, Palette } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Badge, NodeTypeBadge, Button } from '../../ui';
import { useConfigurator } from '../../../hooks/useConfigurator';
import { SERVICES, getServiceById, canServiceRunOnNode } from '../../../data/services';
import { ServiceCategory, DatacenterNode } from '../../../types/configurator';
import { clsx } from 'clsx';
import { useState, useMemo } from 'react';

const CATEGORY_ICONS: Record<ServiceCategory, typeof Server> = {
  infrastructure: Server,
  storage: Database,
  monitoring: Activity,
  workload: Layers,
  ai: Brain,
  rendering: Palette,
};

const CATEGORY_LABELS: Record<ServiceCategory, string> = {
  infrastructure: 'Infrastructure',
  storage: 'Storage',
  monitoring: 'Monitoring',
  workload: 'Workloads',
  ai: 'AI / LLM',
  rendering: 'AI Rendering',
};

export function ServicesStep() {
  const { config, allocateService, deallocateService } = useConfigurator();
  const [showUnallocatedOnly, setShowUnallocatedOnly] = useState(false);

  const categories: ServiceCategory[] = ['infrastructure', 'storage', 'monitoring', 'workload', 'ai', 'rendering'];

  const getServiceAllocation = (serviceId: string) => {
    return config.serviceAllocations.find((a) => a.serviceId === serviceId);
  };

  // Count unallocated services
  const unallocatedCount = useMemo(() => {
    return SERVICES.filter((s) => !config.serviceAllocations.some((a) => a.serviceId === s.id)).length;
  }, [config.serviceAllocations]);

  // Get node memory usage
  const getNodeMemoryUsage = (nodeId: string) => {
    const node = config.nodes.find((n) => n.id === nodeId);
    if (!node) return { used: 0, total: 0, percent: 0 };

    const nodeServices = config.serviceAllocations.filter((a) => a.nodeId === nodeId);
    const usedMemory = nodeServices.reduce((sum, a) => {
      const svc = getServiceById(a.serviceId);
      return sum + (svc?.memoryMB || 0);
    }, 0);
    const totalMemory = node.specs.memoryGB * 1024;
    return {
      used: usedMemory,
      total: totalMemory,
      percent: Math.min(100, (usedMemory / totalMemory) * 100),
    };
  };

  const getNodeById = (nodeId: string): DatacenterNode | undefined => {
    return config.nodes.find((n) => n.id === nodeId);
  };

  const getSuitableNodes = (serviceId: string): DatacenterNode[] => {
    return config.nodes.filter((n) => canServiceRunOnNode(serviceId, n.type));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Allocate Services</h2>
          <p className="mt-1 text-gray-500">
            Assign services to nodes. Critical services are pre-allocated but can be moved.
          </p>
        </div>
        {unallocatedCount > 0 && (
          <Button
            variant={showUnallocatedOnly ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setShowUnallocatedOnly(!showUnallocatedOnly)}
            className="gap-1.5"
          >
            <Filter className="h-4 w-4" />
            Unallocated ({unallocatedCount})
          </Button>
        )}
      </div>

      {categories.map((category) => {
        const allCategoryServices = SERVICES.filter((s) => s.category === category);
        const categoryServices = showUnallocatedOnly
          ? allCategoryServices.filter((s) => !getServiceAllocation(s.id))
          : allCategoryServices;

        // Skip empty categories when filtering
        if (categoryServices.length === 0) return null;
        const Icon = CATEGORY_ICONS[category];

        return (
          <Card key={category}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-hermit-600" />
                <CardTitle>{CATEGORY_LABELS[category]}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-gray-100">
                {categoryServices.map((service) => {
                  const allocation = getServiceAllocation(service.id);
                  const allocatedNode = allocation ? getNodeById(allocation.nodeId) : undefined;
                  const suitableNodes = getSuitableNodes(service.id);
                  const canAllocate = suitableNodes.length > 0;

                  return (
                    <div
                      key={service.id}
                      className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{service.name}</span>
                          {service.critical && (
                            <Badge variant="error" size="sm">
                              Critical
                            </Badge>
                          )}
                          <span className="text-xs text-gray-400">:{service.port}</span>
                        </div>
                        <p className="text-sm text-gray-500">{service.description}</p>
                        <div className="mt-1 flex gap-2 text-xs text-gray-400">
                          <span>{service.memoryMB}MB RAM</span>
                          <span>{service.cpuCores} CPU</span>
                          <span>
                            Runs on: {service.requiredNodeTypes.join(', ')}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 flex items-center gap-2">
                        {allocation && allocatedNode ? (
                          <div className="flex items-center gap-2">
                            <NodeTypeBadge type={allocatedNode.type} />
                            <span className="text-sm text-gray-600">
                              {allocatedNode.hostname}
                            </span>
                            <button
                              onClick={() => deallocateService(service.id, allocation.nodeId)}
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <select
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                allocateService(service.id, e.target.value);
                              }
                            }}
                            disabled={!canAllocate}
                            className={clsx(
                              'rounded-lg border border-gray-300 px-3 py-1.5 text-sm',
                              !canAllocate && 'cursor-not-allowed opacity-50'
                            )}
                          >
                            <option value="">
                              {canAllocate ? 'Select node...' : 'No suitable nodes'}
                            </option>
                            {suitableNodes.map((node) => (
                              <option key={node.id} value={node.id}>
                                {node.hostname} ({node.type})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Allocation Summary */}
      <Card className="bg-gray-50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-hermit-600" />
            <CardTitle>Allocation Summary</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {config.nodes.map((node) => {
              const nodeServices = config.serviceAllocations.filter(
                (a) => a.nodeId === node.id
              );
              const memoryUsage = getNodeMemoryUsage(node.id);
              const isOverAllocated = memoryUsage.percent > 90;

              return (
                <div
                  key={node.id}
                  className={clsx(
                    'rounded-lg bg-white p-3 shadow-sm',
                    isOverAllocated && 'ring-2 ring-amber-400'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <NodeTypeBadge type={node.type} />
                    <span className="font-medium text-gray-900">{node.hostname}</span>
                    {isOverAllocated && (
                      <span title="High memory usage">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      </span>
                    )}
                  </div>

                  {/* Memory usage bar */}
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{nodeServices.length} service{nodeServices.length !== 1 ? 's' : ''}</span>
                      <span>{memoryUsage.used}MB / {memoryUsage.total}MB</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className={clsx(
                          'h-full rounded-full transition-all',
                          memoryUsage.percent > 90
                            ? 'bg-red-500'
                            : memoryUsage.percent > 70
                              ? 'bg-amber-500'
                              : 'bg-hermit-500'
                        )}
                        style={{ width: `${memoryUsage.percent}%` }}
                      />
                    </div>
                  </div>

                  {/* Service list */}
                  <div className="mt-2 space-y-0.5">
                    {nodeServices.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No services allocated</p>
                    ) : (
                      nodeServices.slice(0, 3).map((a) => {
                        const svc = getServiceById(a.serviceId);
                        return (
                          <div key={a.serviceId} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 truncate">{svc?.name}</span>
                            <span className="text-gray-400">{svc?.memoryMB}MB</span>
                          </div>
                        );
                      })
                    )}
                    {nodeServices.length > 3 && (
                      <div className="text-xs text-gray-400">
                        +{nodeServices.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
